import { useState, useCallback, useRef, useEffect } from 'react'
import { Plus, Mic, MoreVertical, Fingerprint, Activity, ShieldCheck, X, Upload, Trash2, Edit3, Volume2, Search, Check, Loader2, MicOff, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/auth'
import { voiceProfilesService } from '@/services/firebase.service'
import { auth } from '@/lib/firebase'

interface VoiceProfile {
  id: string
  name: string
  role: string
  samples: number
  lastActive: string
  status: 'Active' | 'Pending' | 'Training' | 'Error'
  confidence: string
  statusMessage?: string
}

export function VoiceLibrary() {
  const { user } = useAuthStore()
  const [voices, setVoices] = useState<VoiceProfile[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingVoice, setEditingVoice] = useState<VoiceProfile | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [recordingProgress, setRecordingProgress] = useState(0)
  const [newVoice, setNewVoice] = useState({ name: '', role: '' })
  const [_isLoading, setIsLoading] = useState(true)

  const menuRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const recordingTimerRef = useRef<number | null>(null)
  const recordingProfileRef = useRef<string | null>(null) // Ref to hold profile ID during recording
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [microphoneError, setMicrophoneError] = useState<string | null>(null)
  const [showRecordingProfileModal, setShowRecordingProfileModal] = useState(false)

  // Load voice profiles from Firestore
  useEffect(() => {
    const firebaseUid = auth.currentUser?.uid
    if (!user || !firebaseUid) return

    setIsLoading(true)
    const unsubscribe = voiceProfilesService.subscribeAll(firebaseUid, (profiles) => {
      const formattedProfiles: VoiceProfile[] = profiles.map(p => ({
        id: p.id,
        name: p.name,
        role: (p as any).role || 'Non specificato',
        samples: p.sampleCount || 0,
        lastActive: formatTimeAgo(p.lastSeen),
        status: ((p as any).status || 'Pending') as 'Active' | 'Pending' | 'Training' | 'Error',
        confidence: (p as any).confidence || '0%',
        statusMessage: (p as any).statusMessage,
      }))
      setVoices(formattedProfiles)
      setIsLoading(false)
    })

    return () => unsubscribe()
  }, [user])

  // Helper to format time ago
  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Adesso'
    if (diffMins < 60) return `${diffMins} min fa`
    if (diffHours < 24) return `${diffHours} ore fa`
    return `${diffDays} giorni fa`
  }

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filter voices
  const filteredVoices = voices.filter(voice =>
    voice.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    voice.role.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Stats
  const activeProfiles = voices.filter(v => v.status === 'Active').length
  const totalSamples = voices.reduce((acc, v) => acc + v.samples, 0)
  const avgConfidence = voices.length > 0
    ? Math.round(voices.reduce((acc, v) => acc + parseInt(v.confidence), 0) / voices.length)
    : 0

  const handleAddVoice = useCallback(async () => {
    if (!newVoice.name.trim()) {
      toast.error('Inserisci un nome per il profilo')
      return
    }

    const firebaseUid = auth.currentUser?.uid
    if (!user || !firebaseUid) {
      toast.error('Devi essere autenticato')
      return
    }

    try {
      toast.loading('Creazione profilo...', { id: 'create-voice' })
      await voiceProfilesService.create(firebaseUid, {
        name: newVoice.name,
        role: newVoice.role || 'Non specificato'
      })

      setShowAddModal(false)
      setNewVoice({ name: '', role: '' })
      toast.success(`Profilo "${newVoice.name}" creato su Firebase!`, { id: 'create-voice' })
    } catch (error: any) {
      toast.error('Errore: ' + (error.message || 'Creazione fallita'), { id: 'create-voice' })
    }
  }, [newVoice, user])

  const handleEditVoice = useCallback(() => {
    if (!editingVoice) return

    setVoices(prev => prev.map(v =>
      v.id === editingVoice.id ? editingVoice : v
    ))
    setShowEditModal(false)
    setEditingVoice(null)
    toast.success('Profilo aggiornato!')
  }, [editingVoice])

  const handleDeleteVoice = useCallback(async (voice: VoiceProfile) => {
    try {
      toast.loading('Eliminazione profilo...', { id: `delete-${voice.id}` })
      await voiceProfilesService.delete(voice.id)
      setOpenMenuId(null)
      toast.success(`"${voice.name}" rimosso da Firebase.`, { id: `delete-${voice.id}` })
    } catch (error: any) {
      toast.error('Errore: ' + (error.message || 'Eliminazione fallita'), { id: `delete-${voice.id}` })
    }
  }, [])

  const handlePlaySample = useCallback((voice: VoiceProfile) => {
    setOpenMenuId(null)
    toast(`Riproduzione campione di ${voice.name}...`, { icon: '🔊', duration: 2000 })
  }, [])

  const openEditModal = useCallback((voice: VoiceProfile) => {
    setEditingVoice({ ...voice })
    setShowEditModal(true)
    setOpenMenuId(null)
  }, [])

  // Start recording button click - show profile selection first if no profile selected
  const handleStartRecordingClick = useCallback(() => {
    if (voices.length === 0) {
      toast.error('Crea prima un profilo vocale')
      setShowAddModal(true)
      return
    }

    // Always show profile selection modal first
    setShowRecordingProfileModal(true)
  }, [voices.length])

  // Actually start recording with microphone
  const startActualRecording = useCallback(async (profileId: string) => {
    setMicrophoneError(null)

    // Store the profile ID in ref so it's available in onstop handler
    recordingProfileRef.current = profileId
    console.log('Starting recording for profile:', profileId)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      })

      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop())

        const audioBlob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType || 'audio/webm'
        })

        // Use the ref value instead of state (closure issue fix)
        const profileIdToUpload = recordingProfileRef.current
        console.log('Recording stopped, uploading to profile:', profileIdToUpload)

        // Upload the recorded audio
        const firebaseUid = auth.currentUser?.uid
        if (user && firebaseUid && profileIdToUpload && audioBlob.size > 0) {
          const loadingId = `upload-recording-${Date.now()}`
          toast.loading('Caricamento registrazione su Firebase...', { id: loadingId })

          try {
            const extension = mediaRecorder.mimeType?.includes('webm') ? 'webm' : 'mp4'
            const fileName = `recording_${Date.now()}.${extension}`
            const file = new File([audioBlob], fileName, { type: mediaRecorder.mimeType })

            await voiceProfilesService.uploadSample(
              firebaseUid,
              profileIdToUpload,
              file,
              (progress) => {
                console.log(`Upload progress: ${progress}%`)
              }
            )
            toast.success('Campione audio caricato con successo!', { id: loadingId })
          } catch (error: any) {
            console.error('Upload error:', error)
            toast.error('Errore upload: ' + (error.message || 'Upload fallito'), { id: loadingId })
          }
        } else {
          console.warn('Cannot upload - missing data:', { user: !!user, profileIdToUpload, blobSize: audioBlob.size })
          if (audioBlob.size === 0) {
            toast.error('Registrazione vuota - riprova')
          }
        }

        // Reset state
        recordingProfileRef.current = null
        audioChunksRef.current = []
      }

      mediaRecorder.start(1000) // Collect data every second
      setIsRecording(true)
      setRecordingDuration(0)
      toast.success('Registrazione avviata! Parla chiaramente.', { id: 'recording' })

      // Start timer for duration display
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingDuration(prev => {
          const newVal = prev + 1
          // Calculate progress (target 30 seconds minimum)
          setRecordingProgress(Math.min(100, Math.round((newVal / 30) * 100)))
          return newVal
        })
      }, 1000)

    } catch (error: any) {
      console.error('Microphone access error:', error)
      recordingProfileRef.current = null
      setMicrophoneError(
        error.name === 'NotAllowedError'
          ? 'Accesso al microfono negato. Consenti l\'accesso nelle impostazioni del browser.'
          : error.name === 'NotFoundError'
          ? 'Nessun microfono trovato. Collega un microfono e riprova.'
          : 'Errore accesso microfono: ' + error.message
      )
      toast.error('Impossibile accedere al microfono', { id: 'recording' })
    }
  }, [user])

  const handleStopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }

    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current)
      recordingTimerRef.current = null
    }

    setIsRecording(false)
    setRecordingProgress(0)
    setRecordingDuration(0)
  }, [])

  // Format recording duration as mm:ss
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
    }
  }, [])

  const handleUploadSample = useCallback(() => {
    if (voices.length === 0) {
      toast.error('Crea prima un profilo vocale')
      setShowAddModal(true)
      return
    }
    setShowSelectProfileModal(true)
  }, [voices.length])

  const [selectedProfileForUpload, setSelectedProfileForUpload] = useState<string | null>(null)
  const [showSelectProfileModal, setShowSelectProfileModal] = useState(false)

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const firebaseUid = auth.currentUser?.uid
    if (!file || !user || !firebaseUid || !selectedProfileForUpload) {
      toast.error('Seleziona prima un profilo vocale')
      e.target.value = ''
      return
    }

    const loadingId = `upload-${Date.now()}`
    toast.loading(`Caricamento "${file.name}" su Firebase...`, { id: loadingId })

    try {
      await voiceProfilesService.uploadSample(
        firebaseUid,
        selectedProfileForUpload,
        file,
        (progress) => {
          console.log(`Upload progress: ${progress}%`)
        }
      )
      toast.success(`Campione audio caricato con successo!`, { id: loadingId })
      setSelectedProfileForUpload(null)
    } catch (error: any) {
      toast.error('Errore: ' + (error.message || 'Upload fallito'), { id: loadingId })
    }

    e.target.value = ''
  }, [user, selectedProfileForUpload])

  const handleTrainModel = useCallback(async (voice: VoiceProfile) => {
    setOpenMenuId(null)

    if (voice.samples < 3) {
      toast.error(`Servono almeno 3 campioni audio. Attualmente: ${voice.samples}`)
      return
    }

    toast.loading(`Training in corso per ${voice.name}... (1-2 minuti)`, { id: `train-${voice.id}`, duration: 120000 })

    try {
      const result = await voiceProfilesService.train(voice.id)
      if (result.success) {
        toast.success(
          `Training completato! Confidenza: ${result.confidence}% (${result.embeddingsExtracted} campioni)`,
          { id: `train-${voice.id}`, duration: 5000 }
        )
      }
    } catch (error: any) {
      const message = error.message || 'Training fallito'
      toast.error('Errore: ' + message, { id: `train-${voice.id}` })
    }
  }, [])

  return (
    <div className="space-y-8">
      {/* Hidden file input - accepts audio and video formats */}
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*,video/*,.mp4,.webm,.mov,.avi,.mkv,.m4a,.wav,.mp3,.ogg,.flac"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Header Tecnico */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Fingerprint className="h-6 w-6 text-blue-600" />
            Voice Identity Database
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gestisci le impronte vocali biometriche per l'identificazione automatica degli speaker nelle riunioni.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 shadow-lg shadow-slate-900/10 transition-all active:scale-95"
        >
          <Plus className="h-4 w-4" />
          Nuova Impronta Vocale
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Stats Card - Look "Security" */}
        <div className="lg:col-span-3 grid sm:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
              <Activity className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Campioni Totali</p>
              <p className="text-2xl font-bold text-slate-900">{totalSamples}</p>
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Accuratezza Media</p>
              <p className="text-2xl font-bold text-slate-900">{avgConfidence}%</p>
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-violet-50 text-violet-600 rounded-lg">
              <Mic className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Profili Attivi</p>
              <p className="text-2xl font-bold text-slate-900">{activeProfiles}</p>
            </div>
          </div>
        </div>

        {/* Voice List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-slate-800">Profili Registrati</h3>
              <span className="text-xs text-slate-500 bg-white border px-2 py-1 rounded">{voices.length} Totali</span>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca profilo..."
                className="pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-200 focus:border-slate-300 w-48 transition-all"
              />
            </div>
          </div>

          {filteredVoices.length === 0 ? (
            <div className="p-12 text-center">
              <Fingerprint className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Nessun profilo trovato</h3>
              <p className="text-sm text-slate-500 mb-4">
                {searchQuery ? 'Prova una ricerca diversa.' : 'Crea il primo profilo vocale per iniziare.'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" /> Aggiungi Profilo
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredVoices.map((voice) => (
                <div key={voice.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors group">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center relative">
                      <span className="font-bold text-slate-600">{voice.name.charAt(0)}</span>
                      <div className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white ${
                        voice.status === 'Active' ? 'bg-emerald-500' :
                        voice.status === 'Training' ? 'bg-blue-500 animate-pulse' :
                        voice.status === 'Error' ? 'bg-red-500' :
                        'bg-amber-500'
                      }`}></div>
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">{voice.name}</h4>
                      <p className="text-xs text-slate-500">{voice.role} • {voice.samples} campioni audio</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="hidden sm:block text-right">
                      <div className="text-xs text-slate-400 mb-1">
                        {voice.status === 'Training' ? 'Training...' :
                         voice.status === 'Error' ? 'Errore' : 'Affidabilita'}
                      </div>
                      <div className={`flex items-center gap-1 text-sm font-bold ${
                        voice.status === 'Training' ? 'text-blue-600' :
                        voice.status === 'Error' ? 'text-red-600' :
                        parseInt(voice.confidence) > 90 ? 'text-emerald-600' :
                        parseInt(voice.confidence) > 70 ? 'text-slate-700' :
                        'text-amber-600'
                      }`}>
                        {voice.status === 'Training' ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : voice.status === 'Error' ? (
                          <AlertCircle className="h-3 w-3" />
                        ) : (
                          <Activity className="h-3 w-3" />
                        )}
                        {voice.status === 'Error' ? 'Riprova' : voice.confidence}
                      </div>
                    </div>
                    <div className="relative" ref={openMenuId === voice.id ? menuRef : null}>
                      <button
                        onClick={() => setOpenMenuId(openMenuId === voice.id ? null : voice.id)}
                        className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {openMenuId === voice.id && (
                        <div className="absolute top-full right-0 mt-2 w-48 glass-dropdown z-[200] overflow-hidden">
                          <button
                            onClick={() => {
                              setSelectedProfileForUpload(voice.id)
                              setOpenMenuId(null)
                              fileInputRef.current?.click()
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-emerald-600 hover:bg-emerald-50 flex items-center gap-3"
                          >
                            <Upload className="h-4 w-4" /> Carica campione audio
                          </button>
                          <button
                            onClick={() => handlePlaySample(voice)}
                            className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3"
                          >
                            <Volume2 className="h-4 w-4 text-slate-400" /> Riproduci campione
                          </button>
                          <button
                            onClick={() => openEditModal(voice)}
                            className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3"
                          >
                            <Edit3 className="h-4 w-4 text-slate-400" /> Modifica profilo
                          </button>
                          {voice.status !== 'Active' && (
                            <button
                              onClick={() => handleTrainModel(voice)}
                              className="w-full px-4 py-2.5 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-3"
                            >
                              <Activity className="h-4 w-4" /> {voice.status === 'Training' ? 'Forza training' : voice.status === 'Error' ? 'Riprova training' : 'Avvia training'}
                            </button>
                          )}
                          <div className="border-t border-slate-100" />
                          <button
                            onClick={() => handleDeleteVoice(voice)}
                            className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                          >
                            <Trash2 className="h-4 w-4" /> Elimina profilo
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar: Add New (Biometric Scan Visual) */}
        <div className="bg-slate-900 rounded-xl p-6 text-white relative overflow-hidden flex flex-col items-center justify-center text-center">
          <div className="absolute inset-0 bg-blue-600/10">
            {/* Fake scanning grid lines */}
            <div className="absolute w-full h-px bg-blue-500/30 top-1/4"></div>
            <div className="absolute w-full h-px bg-blue-500/30 bottom-1/4"></div>
            <div className="absolute h-full w-px bg-blue-500/30 left-1/4"></div>
            <div className="absolute h-full w-px bg-blue-500/30 right-1/4"></div>
          </div>

          <div className={`relative z-10 p-4 rounded-full border mb-4 transition-all ${
            isRecording
              ? 'bg-red-500/20 border-red-500/50 shadow-[0_0_30px_rgba(239,68,68,0.5)] animate-pulse'
              : 'bg-slate-800/80 border-blue-500/30 shadow-[0_0_30px_rgba(59,130,246,0.3)]'
          }`}>
            {isRecording ? (
              <MicOff className="h-8 w-8 text-red-400" />
            ) : (
              <Mic className="h-8 w-8 text-blue-500" />
            )}
          </div>

          {isRecording && (
            <div className="relative z-10 w-full mb-4">
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-red-500 rounded-full transition-all duration-300"
                  style={{ width: `${recordingProgress}%` }}
                />
              </div>
              <p className="text-xs text-slate-400 mt-2 font-mono">
                {formatDuration(recordingDuration)} {recordingProgress < 100 && `(min. 30s)`}
              </p>
            </div>
          )}

          {microphoneError && (
            <div className="relative z-10 w-full mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
              <p className="text-xs text-red-300 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                {microphoneError}
              </p>
            </div>
          )}

          <h3 className="relative z-10 text-lg font-bold mb-2">
            {isRecording ? 'Registrazione in corso...' : 'Aggiungi Voce'}
          </h3>
          <p className="relative z-10 text-slate-400 text-sm mb-6">
            {isRecording
              ? 'Parla chiaramente per almeno 30 secondi per un risultato ottimale.'
              : 'Carica un file audio o registra ora per addestrare il modello biometrico su un nuovo speaker.'}
          </p>

          {isRecording ? (
            <button
              onClick={handleStopRecording}
              className="relative z-10 w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <MicOff className="h-4 w-4" /> Ferma e Salva
            </button>
          ) : (
            <>
              <button
                onClick={handleStartRecordingClick}
                className="relative z-10 w-full bg-blue-600 hover:bg-blue-500 text-white py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Mic className="h-4 w-4" /> Avvia Registrazione
              </button>
              <button
                onClick={handleUploadSample}
                className="relative z-10 w-full mt-3 bg-transparent hover:bg-white/10 text-white border border-white/20 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Upload className="h-4 w-4" /> Carica Campione Audio
              </button>
            </>
          )}
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-lg text-slate-900">Nuovo Profilo Vocale</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Nome Completo *</label>
                <input
                  type="text"
                  value={newVoice.name}
                  onChange={(e) => setNewVoice(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Es. Mario Rossi"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 focus:border-slate-400 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Ruolo / Posizione</label>
                <input
                  type="text"
                  value={newVoice.role}
                  onChange={(e) => setNewVoice(prev => ({ ...prev, role: e.target.value }))}
                  placeholder="Es. Product Manager"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 focus:border-slate-400 transition-all"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleAddVoice}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-2"
              >
                <Check className="h-4 w-4" /> Crea Profilo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingVoice && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-lg text-slate-900">Modifica Profilo</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Nome Completo</label>
                <input
                  type="text"
                  value={editingVoice.name}
                  onChange={(e) => setEditingVoice(prev => prev ? { ...prev, name: e.target.value } : null)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 focus:border-slate-400 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Ruolo / Posizione</label>
                <input
                  type="text"
                  value={editingVoice.role}
                  onChange={(e) => setEditingVoice(prev => prev ? { ...prev, role: e.target.value } : null)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 focus:border-slate-400 transition-all"
                />
              </div>
              <div className="pt-2 flex items-center gap-4 text-sm text-slate-500">
                <span>{editingVoice.samples} campioni</span>
                <span>•</span>
                <span>Affidabilita: {editingVoice.confidence}</span>
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleEditVoice}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-2"
              >
                <Check className="h-4 w-4" /> Salva Modifiche
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Select Profile Modal for Sidebar Upload */}
      {showSelectProfileModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-lg text-slate-900">Seleziona Profilo</h3>
              <button
                onClick={() => setShowSelectProfileModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto">
              <p className="text-sm text-slate-500 mb-4">
                Seleziona il profilo vocale a cui associare il campione audio:
              </p>
              <div className="space-y-2">
                {voices.map((voice) => (
                  <button
                    key={voice.id}
                    onClick={() => {
                      setSelectedProfileForUpload(voice.id)
                      setShowSelectProfileModal(false)
                      setTimeout(() => fileInputRef.current?.click(), 100)
                    }}
                    className="w-full p-3 text-left bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-3"
                  >
                    <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="font-bold text-slate-600">{voice.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{voice.name}</p>
                      <p className="text-xs text-slate-500">{voice.role} • {voice.samples} campioni</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Select Profile Modal for Recording */}
      {showRecordingProfileModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-lg text-slate-900">Seleziona Profilo per Registrazione</h3>
              <button
                onClick={() => setShowRecordingProfileModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto">
              <p className="text-sm text-slate-500 mb-4">
                Seleziona il profilo vocale a cui associare la registrazione:
              </p>
              <div className="space-y-2">
                {voices.map((voice) => (
                  <button
                    key={voice.id}
                    onClick={() => {
                      // Close modal first
                      setShowRecordingProfileModal(false)
                      // Start recording immediately with the profile ID
                      startActualRecording(voice.id)
                    }}
                    className="w-full p-3 text-left bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-3"
                  >
                    <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="font-bold text-slate-600">{voice.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{voice.name}</p>
                      <p className="text-xs text-slate-500">{voice.role} • {voice.samples} campioni</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
